import React, { FC, useContext, useEffect, useState } from "react";
import { CircularProgress, createStyles, Theme, WithStyles, withStyles } from "@material-ui/core";
import { Form } from "react-final-form";
import { TextField } from "../../../lib/components/form/TextField";
import { GreenButton, GreyButton } from "../../../lib/components/ColorButtons";
import { productcharacteristics, ProductCharacteristics } from "../../../lib/api/productcharacteristics";
import { SnackContext } from "../../../lib/context/SnackContext";
import { GenerateErrorMessage } from "../../../lib/helpers/string_methods";
import { SelectField } from "../../../lib/components/form/SelectField";
import { productgroup } from "../../../lib/api/productgroup";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "2rem",
    },
    container: {
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
    },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      flexDirection: "row",
      gap: "0.25rem",
    },
    submitButton: {
      display: "flex",
      flexDirection: "row",
    },
  });

type LabelingFormProps = {
  id: number;
  handleClose(): void;
} & WithStyles<typeof styles>;

const LabelingFormUnstyled: FC<LabelingFormProps> = ({ id, classes, handleClose }) => {
  const { updateSnack } = useContext(SnackContext);
  const [initData, setInitData] = useState<Partial<ProductCharacteristics>>({});
  const [productGroupsData, setProductGroupsData] = useState([]);

  const loadData = async () => {
    try {
      if (id > 0) {
        const result = await productcharacteristics.single(id);
        setInitData(result);
      }
      const productsGroupResult = await productgroup.all();
      console.log("🚀 ~ loadData ~ productsGroupResult:", productsGroupResult);
      setProductGroupsData(productsGroupResult.map((row) => ({ display: row.group_code, value: row.id })));
    } catch (error) {
      const err = GenerateErrorMessage(error, "Failed to fetch data. Please try again");
      updateSnack({ show: true, color: "red", message: err });
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  return (
    <div>
      <Form
        initialValues={{ ...initData }}
        validate={(values) => {
          const errors: { [key: string]: string } = {};

          if ((values.characteristics_code || "").length > 3) {
            errors.characteristics_code = "Length cannot exceed 3 characters.";
          }
          if ((values.characteristics_description || "").length > 255) {
            errors.characteristics_description = "Length cannot exceed 255 characters.";
          }

          return errors;
        }}
        onSubmit={async (values: ProductCharacteristics) => {
          try {
            if (id > 0) {
              delete values["id"];
              await productcharacteristics.update(id, { data: values });
            } else {
              await productcharacteristics.create({ data: values });
            }
            updateSnack({ show: true, color: "green", message: "Successfully submitted!" });
            handleClose();
          } catch (error) {
            const err = GenerateErrorMessage(error, "Failed to submit. Please try again");
            updateSnack({ show: true, color: "red", message: err });
          }
        }}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit} className={classes.form}>
            <div className={classes.container}>
              <SelectField field="labelinggroup_id" title="Product Group" data={productGroupsData} />
              <TextField field="characteristic_code" title="Characteristics Code" required />
              <TextField field="characteristic_description" title="Characteristics Description" required />
            </div>
            <div className={classes.actions}>
              <GreyButton variant="contained" type="button" disabled={submitting} onClick={handleClose}>
                Cancel
              </GreyButton>
              <GreenButton variant="contained" type="submit" disabled={submitting} className={classes.submitButton}>
                {submitting && <CircularProgress size={24} style={{ marginRight: "0.5rem" }} />}
                <span>Submit</span>
              </GreenButton>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export const LabelingForm = withStyles(styles)(LabelingFormUnstyled);
